import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { api } from '../lib/api';
import { LANGUAGES } from '../i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Flame, Sparkles, Dumbbell, Globe } from 'lucide-react';
import { toast } from 'sonner';

const HERO_IMG = 'https://images.unsplash.com/photo-1549505415-e16dbd446231?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';

export default function AuthPage() {
  const { login, t, lang, setLang } = useApp();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', username: '', full_name: '' });
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = mode === 'login'
        ? { email: form.email, password: form.password }
        : form;
      const res = await api.post(`/auth/${mode}`, payload);
      login(res.data.token, res.data.user);
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen bg-background flex">
      {/* Hero side */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <img src={HERO_IMG} alt="athlete" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-background/75" />
        <div className="absolute inset-0 hero-sheen" />
        <div className="relative z-10 h-full flex flex-col justify-center px-14">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-primary/15 glow-fitness flex items-center justify-center">
              <Flame size={26} className="text-primary" />
            </div>
            <span className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>FitCore</span>
          </div>
          <h1 className="text-5xl font-semibold tracking-tight leading-tight mb-5" style={{ fontFamily: 'Space Grotesk' }}>
            Train together.<br />
            <span className="text-primary">Powered by AI.</span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-md mb-8">
            The free sports social network with an AI coach, food scanner and global fitness community. 100% free, forever.
          </p>
          <div className="flex gap-3">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border text-sm">
              <Dumbbell size={15} className="text-primary" /> 130+ exercises
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border text-sm">
              <Sparkles size={15} className="text-[hsl(205,100%,56%)]" /> AI Coach
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border text-sm">
              <Globe size={15} className="text-muted-foreground" /> 12 languages
            </div>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <div className="w-10 h-10 rounded-xl bg-primary/15 glow-fitness flex items-center justify-center">
              <Flame size={22} className="text-primary" />
            </div>
            <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>FitCore</span>
          </div>

          <div className="flex justify-end mb-4">
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger data-testid="auth-language-select" className="w-32 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((l) => (
                  <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <h2 className="text-2xl font-semibold mb-1" style={{ fontFamily: 'Space Grotesk' }}>
            {mode === 'login' ? t('welcomeBack') : t('joinFitCore')}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {mode === 'login' ? 'Log in to continue your fitness journey' : 'Free forever. No credit card needed.'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <>
                <div className="space-y-1.5">
                  <Label>{t('fullName')}</Label>
                  <Input data-testid="auth-fullname-input" value={form.full_name} onChange={set('full_name')} required minLength={2} autoComplete="name" placeholder="Alex Strong" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('username')}</Label>
                  <Input data-testid="auth-username-input" value={form.username} onChange={set('username')} required minLength={3} autoComplete="username" placeholder="alexstrong" />
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label>{t('email')}</Label>
              <Input data-testid="auth-email-input" type="email" value={form.email} onChange={set('email')} required autoComplete="email" placeholder="you@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label>{t('password')}</Label>
              <Input data-testid="auth-password-input" type="password" value={form.password} onChange={set('password')} required minLength={6} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} placeholder="••••••••" />
            </div>
            <Button data-testid="auth-submit-button" type="submit" disabled={busy} className="w-full font-semibold">
              {busy ? t('loading') : mode === 'login' ? t('login') : t('register')}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button
              data-testid="auth-toggle-mode-button"
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="text-primary font-medium hover:underline"
            >
              {mode === 'login' ? t('register') : t('login')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
